/**
 * „Prüf-Serie“ und Punkte.
 *
 * Belohnt wird pünktliches PRÜFEN – auch „noch feucht, nicht gegossen“ zählt.
 * Eine Gieß-Serie würde Übergießen belohnen, deshalb gibt es sie bewusst nicht.
 */
import type { CareEvent, Settings, TaskType } from './model';
import { addDaysToKey, dayKeyDiff, localDateKey } from './time';

export interface Level {
  min: number;
  name: string;
  icon: string;
}

export const LEVELS: Level[] = [
  { min: 0, name: 'Samenkorn', icon: '🌰' },
  { min: 40, name: 'Keimling', icon: '🌱' },
  { min: 120, name: 'Jungpflanze', icon: '🌿' },
  { min: 300, name: 'Grüner Daumen', icon: '👍' },
  { min: 700, name: 'Pflanzenflüsterer', icon: '🍃' },
  { min: 1500, name: 'Urwald-Legende', icon: '🌳' },
];

export interface Badge {
  key: string;
  icon: string;
  title: string;
  description: string;
  earned: boolean;
}

export interface StreakInfo {
  /** Tage in Folge ohne verpasste Prüfung. */
  current: number;
  best: number;
  points: number;
  level: Level;
  nextLevel: Level | null;
  /** Anteil bis zum nächsten Level (0–1). */
  progress: number;
  /** Wie viele Prüfungen „noch feucht“ ergaben (= Wasser gespart). */
  savedWaterings: number;
  checks: number;
  badges: Badge[];
}

/** Toleranz: bis zu 1 Tag später gilt noch als pünktlich. */
export const ON_TIME_GRACE_DAYS = 1;

function onTime(e: CareEvent, tz: string): boolean {
  if (!e.dueKey) return true;
  return dayKeyDiff(e.dueKey, localDateKey(new Date(e.at), tz)) <= ON_TIME_GRACE_DAYS;
}

export function pointsFor(e: CareEvent, type: TaskType | undefined, tz: string): number {
  if (e.deletedAt) return 0;
  if (e.outcome === 'snoozed' || e.outcome === 'skipped') return 0;
  let p = type?.points ?? 5;
  if (onTime(e, tz)) p += 2;
  // Ehrlicher Fingertest („noch feucht“) wird extra belohnt
  if (e.outcome === 'still_moist') p += 3;
  return p;
}

/**
 * Serie: Ein Tag „zählt“, wenn an diesem Tag geprüft wurde ODER nichts fällig war.
 * Sie reißt nur, wenn eine Aufgabe mehr als ON_TIME_GRACE_DAYS überfällig wurde,
 * ohne dass geprüft wurde. Vereinfacht über die dueKeys der Ereignisse berechnet.
 */
export function streakInfo(events: CareEvent[], taskTypes: TaskType[], settings: Settings, now: Date): StreakInfo {
  const tz = settings.timezone;
  const types = new Map(taskTypes.map((t) => [t.key, t]));
  const active = events.filter((e) => !e.deletedAt && (e.outcome === 'done' || e.outcome === 'still_moist'));
  const points = active.reduce((s, e) => s + pointsFor(e, types.get(e.typeKey), tz), 0);

  // Tage, an denen zu spät geprüft wurde → brechen die Serie
  const lateDays = new Set<string>();
  const activeDays = new Set<string>();
  for (const e of active) {
    const day = localDateKey(new Date(e.at), tz);
    activeDays.add(day);
    if (!onTime(e, tz) && e.dueKey) lateDays.add(addDaysToKey(e.dueKey, ON_TIME_GRACE_DAYS + 1));
  }
  const first = [...activeDays].sort()[0];
  const today = localDateKey(now, tz);
  let current = 0;
  let best = 0;
  let run = 0;
  if (first) {
    for (let d = first; d <= today; d = addDaysToKey(d, 1)) {
      if (lateDays.has(d)) {
        run = 0;
      } else {
        run += 1;
      }
      best = Math.max(best, run);
    }
    current = run;
  }

  const levelIdx = LEVELS.reduce((idx, l, i) => (points >= l.min ? i : idx), 0);
  const level = LEVELS[levelIdx]!;
  const nextLevel = LEVELS[levelIdx + 1] ?? null;
  const progress = nextLevel ? (points - level.min) / (nextLevel.min - level.min) : 1;
  const savedWaterings = active.filter((e) => e.outcome === 'still_moist').length;
  const checks = active.length;
  const withPhoto = events.filter((e) => e.photoId && !e.deletedAt).length;
  const viaShortcut = events.filter((e) => ['nfc', 'siri', 'shortcut'].includes(e.source)).length;

  const badges: Badge[] = [
    { key: 'first', icon: '🌱', title: 'Erster Check', description: 'Die erste Pflanze geprüft.', earned: checks >= 1 },
    { key: 'honest', icon: '🧐', title: 'Ehrlicher Finger', description: '5× „noch feucht“ gesagt statt blind zu gießen.', earned: savedWaterings >= 5 },
    { key: 'week', icon: '📅', title: 'Eine Woche dran', description: '7 Tage in Folge alles pünktlich.', earned: best >= 7 },
    { key: 'month', icon: '🗓️', title: 'Ein Monat dran', description: '30 Tage in Folge alles pünktlich.', earned: best >= 30 },
    { key: 'photo', icon: '📸', title: 'Fotograf', description: '10 Fotos im Tagebuch.', earned: withPhoto >= 10 },
    { key: 'nfc', icon: '📲', title: 'Zauberstab', description: 'Per NFC oder Siri abgehakt.', earned: viaShortcut >= 1 },
    { key: 'hundred', icon: '💯', title: 'Hundert', description: '100 Prüfungen.', earned: checks >= 100 },
  ];

  return { current, best, points, level, nextLevel, progress: Math.max(0, Math.min(1, progress)), savedWaterings, checks, badges };
}
